/**
 * Decision Engine — wymiar E spinajacy 4 wymiary (quality, SEO, cluster_fit, cannib)
 * w jedna z 7 finalnych akcji.
 *
 * Deterministyczna funkcja TS. ZERO LLM. <1s dla 88 wpisow.
 *
 * LUKA #1: refurbish_* wymaga matched_node. Bez noda -> monitor z 3 opcjami.
 * Precompute Set<postId> w batch.
 *
 * Locked mechanism: user zaakceptowal akcje -> final_action_locked=true ->
 * auto-recompute skipuje tego wpisu. Cascade refurbish_approved_by_user=true
 * dla refurbish_*.
 */

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";

const PREFIX = "[DECISION-ENGINE]";

export type FinalAction =
  | "keep"
  | "refurbish"
  | "refurbish_url_preserve"
  | "keep_outer"
  | "delete"
  | "consolidate_301"
  | "monitor";

export type FinalActionPriority = "high" | "medium" | "low";

export interface DecisionInput {
  id: string;
  quality_recommendation: "keep" | "borderline" | "refurbish" | null;
  cluster_fit: "in_scope" | "adjacent" | "off_topic" | null;
  seo_organic_traffic: number | null;
  seo_top_3_count: number | null;
  seo_top_10_count: number | null;
  seo_top_50_count: number | null;
  seo_content_decay: boolean | null;
  seo_lost_keywords_count: number | null;
  seo_has_cannibalization: boolean | null;
  has_matched_node: boolean;
}

export interface DecisionResult {
  action: FinalAction;
  priority: FinalActionPriority;
  reason: string;
}

// ============================================================================
// HELPERS (progi hardcoded — BACKLOG: per-map override w thematic_maps JSONB)
// ============================================================================

function hasStrongSEO(p: DecisionInput): boolean {
  return (
    (p.seo_top_3_count ?? 0) >= 2 ||
    (p.seo_top_10_count ?? 0) >= 5 ||
    (p.seo_organic_traffic ?? 0) >= 50
  );
}

function hasSEO(p: DecisionInput): boolean {
  return (
    (p.seo_top_3_count ?? 0) >= 1 ||
    (p.seo_top_10_count ?? 0) >= 2 ||
    (p.seo_organic_traffic ?? 0) >= 20
  );
}

function hasAnySEO(p: DecisionInput): boolean {
  return (p.seo_top_50_count ?? 0) > 0 || (p.seo_organic_traffic ?? 0) > 0;
}

function hasDecay(p: DecisionInput): boolean {
  return p.seo_content_decay === true && (p.seo_lost_keywords_count ?? 0) >= 3;
}

const NO_NODE_REASON =
  "Kwalifikuje się do refurbish, ale brak noda w mapie zmatchowanego z tym wpisem. " +
  "Opcje: (a) wygeneruj nowy node z tematyki wpisu w Content Engine, " +
  "(b) zmapuj ręcznie do istniejącego noda przez SRO Matcher, " +
  "(c) pozostaw wpis in-place bez refurbish.";

// ============================================================================
// CORE — pure function, zero side effects
// ============================================================================

export function computeFinalAction(p: DecisionInput): DecisionResult {
  // 1. KANIBALIZACJA — priorytet nad wszystkim
  if (p.seo_has_cannibalization) {
    return {
      action: "consolidate_301",
      priority: "high",
      reason: "Kanibalizacja wykryta — wybierz winner, loser 301 redirect.",
    };
  }

  // 2. OFF_TOPIC
  if (p.cluster_fit === "off_topic") {
    if (hasAnySEO(p)) {
      return {
        action: "keep_outer",
        priority: "low",
        reason: "Off-topic ale rankuje. Nie integruj z mapą, nie dotykaj.",
      };
    }
    return {
      action: "delete",
      priority: "medium",
      reason: "Off-topic + zero SEO — brak wartości, do usunięcia.",
    };
  }

  // 3. ADJACENT
  if (p.cluster_fit === "adjacent") {
    if (hasSEO(p) || hasStrongSEO(p)) {
      return {
        action: "keep_outer",
        priority: "low",
        reason: "Adjacent z ruchem — outer cluster, buduje autorytet.",
      };
    }
    if (p.quality_recommendation === "refurbish" && !hasAnySEO(p)) {
      return {
        action: "delete",
        priority: "low",
        reason: "Adjacent + słaba + bez SEO — usuń.",
      };
    }
    return {
      action: "monitor",
      priority: "low",
      reason: "Adjacent bez silnych sygnałów — ręczna decyzja.",
    };
  }

  // 4. IN_SCOPE (main path)
  if (p.cluster_fit === "in_scope") {
    // 4a. decay — pilny refresh
    if (hasDecay(p)) {
      return {
        action: "refurbish_url_preserve",
        priority: "high",
        reason: `Content decay — ${p.seo_lost_keywords_count} keywords utraconych, pilny refresh. URL MUSI zostać.`,
      };
    }

    // 4b. keep quality
    if (p.quality_recommendation === "keep") {
      if (hasAnySEO(p)) {
        return {
          action: "keep",
          priority: "low",
          reason: "Dobra treść + widoczność — zachowaj.",
        };
      }
      return {
        action: "monitor",
        priority: "low",
        reason: "Dobra ale nie rankuje — sprawdź indeksację.",
      };
    }

    // 4c. refurbish quality
    if (p.quality_recommendation === "refurbish") {
      if (hasSEO(p)) {
        if (!p.has_matched_node) {
          return { action: "monitor", priority: "medium", reason: NO_NODE_REASON };
        }
        return {
          action: "refurbish_url_preserve",
          priority: "high",
          reason: "Słaba treść + rankuje — przepisz, URL MUSI zostać.",
        };
      }
      if (!p.has_matched_node) {
        return { action: "monitor", priority: "low", reason: NO_NODE_REASON };
      }
      return {
        action: "refurbish",
        priority: "medium",
        reason: "Słaba treść + zero SEO — przepisz, URL elastyczny.",
      };
    }

    // 4d. borderline
    if (p.quality_recommendation === "borderline") {
      if (hasStrongSEO(p)) {
        return {
          action: "keep",
          priority: "low",
          reason: "Borderline + mocny ranking — nie ryzykuj zmiany.",
        };
      }
      if (hasSEO(p)) {
        if (!p.has_matched_node) {
          return { action: "monitor", priority: "medium", reason: NO_NODE_REASON };
        }
        return {
          action: "refurbish_url_preserve",
          priority: "medium",
          reason: "Borderline + ma ruch — przepisz, URL zostaje.",
        };
      }
      if (!hasAnySEO(p)) {
        if (!p.has_matched_node) {
          return { action: "monitor", priority: "low", reason: NO_NODE_REASON };
        }
        return {
          action: "refurbish",
          priority: "medium",
          reason: "Borderline + zero widoczności — przepisz bez sentymentu do URL.",
        };
      }
      return {
        action: "monitor",
        priority: "low",
        reason: "Borderline + minimal visibility — sprawdź ręcznie.",
      };
    }
  }

  // 5. FALLBACK
  return {
    action: "monitor",
    priority: "low",
    reason: "Edge case — sprawdź ręcznie.",
  };
}

// ============================================================================
// BATCH — recompute dla website
// ============================================================================

interface DecisionRow extends DecisionInput {
  final_action: FinalAction | null;
  final_action_locked: boolean;
}

export interface FinalActionBatchStats {
  processed: number;
  updated: number;
  unchanged: number;
  locked_skipped: number;
  distribution: Record<FinalAction, number>;
  duration_ms: number;
}

type LogCb = (
  type: "info" | "success" | "error" | "stage",
  message: string,
  debug?: Record<string, unknown>,
) => void;

async function loadMatchedPostIdsForWebsite(websiteId: string): Promise<Set<string>> {
  // Wszystkie mapy z tego website
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: maps } = await (supabase as any)
    .from("thematic_maps")
    .select("id")
    .eq("website_id", websiteId);

  const mapIds = ((maps || []) as Array<{ id: string }>).map((m) => m.id);
  if (mapIds.length === 0) return new Set();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("matched_wp_post_id")
    .in("map_id", mapIds)
    .not("matched_wp_post_id", "is", null);

  return new Set(
    ((nodes || []) as Array<{ matched_wp_post_id: string }>).map((r) => r.matched_wp_post_id),
  );
}

export async function recomputeFinalActionsForWebsite(
  websiteId: string,
  options: {
    onLog?: LogCb;
    forceUnlocked?: boolean; // gdy true → recompute rowniez dla locked=true
  } = {},
): Promise<FinalActionBatchStats> {
  const t0 = Date.now();
  const forceUnlocked = options.forceUnlocked ?? false;

  // 1. Precompute matched_node Set (LUKA #1)
  const matchedSet = await loadMatchedPostIdsForWebsite(websiteId);

  // 2. Load wpisy z 4 wymiarami
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, quality_recommendation, cluster_fit, seo_organic_traffic, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_content_decay, seo_lost_keywords_count, seo_has_cannibalization, final_action, final_action_locked",
    )
    .eq("website_id", websiteId);

  if (error) throw error;
  const rows = (data || []) as Array<{
    id: string;
    quality_recommendation: DecisionInput["quality_recommendation"];
    cluster_fit: DecisionInput["cluster_fit"];
    seo_organic_traffic: number | null;
    seo_top_3_count: number | null;
    seo_top_10_count: number | null;
    seo_top_50_count: number | null;
    seo_content_decay: boolean | null;
    seo_lost_keywords_count: number | null;
    seo_has_cannibalization: boolean | null;
    final_action: FinalAction | null;
    final_action_locked: boolean | null;
  }>;

  const total = rows.length;
  logPublic(
    PREFIX,
    `Start recompute for website ${websiteId.slice(0, 8)} — ${total} wpisów, matched_set=${matchedSet.size}, forceUnlocked=${forceUnlocked}`,
  );
  options.onLog?.(
    "info",
    `▶ Decision Engine: recompute ${total} wpisów (matched_set=${matchedSet.size})`,
  );

  const stats: FinalActionBatchStats = {
    processed: 0,
    updated: 0,
    unchanged: 0,
    locked_skipped: 0,
    distribution: {
      keep: 0,
      refurbish: 0,
      refurbish_url_preserve: 0,
      keep_outer: 0,
      delete: 0,
      consolidate_301: 0,
      monitor: 0,
    },
    duration_ms: 0,
  };

  const nowIso = new Date().toISOString();

  for (const row of rows) {
    stats.processed++;

    if (row.final_action_locked === true && !forceUnlocked) {
      stats.locked_skipped++;
      // Licznik distribution dla locked tez
      if (row.final_action) stats.distribution[row.final_action]++;
      continue;
    }

    const input: DecisionInput = {
      id: row.id,
      quality_recommendation: row.quality_recommendation,
      cluster_fit: row.cluster_fit,
      seo_organic_traffic: row.seo_organic_traffic,
      seo_top_3_count: row.seo_top_3_count,
      seo_top_10_count: row.seo_top_10_count,
      seo_top_50_count: row.seo_top_50_count,
      seo_content_decay: row.seo_content_decay,
      seo_lost_keywords_count: row.seo_lost_keywords_count,
      seo_has_cannibalization: row.seo_has_cannibalization,
      has_matched_node: matchedSet.has(row.id),
    };

    const result = computeFinalAction(input);
    stats.distribution[result.action]++;

    // Update tylko gdy zmienione
    if (
      row.final_action !== result.action ||
      row.final_action === null /* pierwszy compute */
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: updErr } = await (supabase as any)
        .from("wp_posts")
        .update({
          final_action: result.action,
          final_action_priority: result.priority,
          final_action_reason: result.reason,
          final_action_computed_at: nowIso,
        })
        .eq("id", row.id);
      if (updErr) {
        logError(PREFIX, `Update ${row.id}: ${updErr.message}`);
      } else {
        stats.updated++;
      }
    } else {
      stats.unchanged++;
      // Aktualizuj tylko reason/priority jesli sie zmienily (edge case)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from("wp_posts")
        .update({
          final_action_priority: result.priority,
          final_action_reason: result.reason,
          final_action_computed_at: nowIso,
        })
        .eq("id", row.id);
    }
  }

  stats.duration_ms = Date.now() - t0;

  logPublic(
    PREFIX,
    `Recompute done ${stats.duration_ms}ms: updated=${stats.updated} unchanged=${stats.unchanged} locked_skipped=${stats.locked_skipped}`,
  );
  options.onLog?.(
    "stage",
    `✓ Decision Engine done ${(stats.duration_ms / 1000).toFixed(2)}s: updated=${stats.updated} locked=${stats.locked_skipped} · keep=${stats.distribution.keep} refurbish=${stats.distribution.refurbish} refurbish_url=${stats.distribution.refurbish_url_preserve} keep_outer=${stats.distribution.keep_outer} delete=${stats.distribution.delete} cannib=${stats.distribution.consolidate_301} monitor=${stats.distribution.monitor}`,
    { stats },
  );

  logDebug(PREFIX, `progress ${stats.processed}/${total}`);

  // M16 auto-run: L5 coverage refresh po recompute final_actions
  try {
    const { auditMapCoverageOnly } = await import("@/services/validation/mapAuditOrchestrator");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: maps } = await (supabase as any)
      .from("thematic_maps").select("id").eq("website_id", websiteId);
    const mapIds = ((maps || []) as Array<{ id: string }>).map((m) => m.id);
    for (const mapId of mapIds) {
      await auditMapCoverageOnly(mapId, { onLog: options.onLog });
    }
  } catch (err) {
    logError(PREFIX, `L5 coverage recompute failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  return stats;
}

// ============================================================================
// ACCEPT / OVERRIDE / UNLOCK / BULK
// ============================================================================

async function cascadeRefurbishApproval(
  wpPostId: string,
  userId: string | null | undefined,
  action: FinalAction,
): Promise<boolean> {
  if (action !== "refurbish" && action !== "refurbish_url_preserve") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("thematic_nodes")
    .update({
      refurbish_approved_by_user: true,
      refurbish_approved_at: new Date().toISOString(),
      refurbish_approved_by: userId ?? null,
    })
    .eq("matched_wp_post_id", wpPostId)
    .select("id");
  if (error) {
    logError(PREFIX, `Cascade refurbish approval ${wpPostId}: ${error.message}`);
    return false;
  }
  return (data || []).length > 0;
}

export async function acceptFinalAction(
  wpPostId: string,
  userId?: string | null,
): Promise<{ action: FinalAction | null; cascaded: boolean }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: before } = await (supabase as any)
    .from("wp_posts")
    .select("final_action")
    .eq("id", wpPostId)
    .maybeSingle();

  const action = (before as { final_action: FinalAction | null } | null)?.final_action ?? null;
  if (!action) {
    throw new Error("Brak final_action dla wpisu — najpierw uruchom Decision Engine.");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts")
    .update({
      final_action_locked: true,
      final_action_computed_at: new Date().toISOString(),
    })
    .eq("id", wpPostId);
  if (error) throw error;

  const cascaded = await cascadeRefurbishApproval(wpPostId, userId, action);
  return { action, cascaded };
}

export async function overrideFinalAction(
  wpPostId: string,
  newAction: FinalAction,
  reason?: string,
  userId?: string | null,
): Promise<{ cascaded: boolean }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts")
    .update({
      final_action: newAction,
      final_action_reason: reason ?? `Manual override → ${newAction}`,
      final_action_locked: true,
      final_action_user_override: true,
      final_action_computed_at: new Date().toISOString(),
    })
    .eq("id", wpPostId);
  if (error) throw error;

  const cascaded = await cascadeRefurbishApproval(wpPostId, userId, newAction);
  return { cascaded };
}

export async function unlockFinalAction(wpPostId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts")
    .update({
      final_action_locked: false,
      final_action_user_override: false,
    })
    .eq("id", wpPostId);
  if (error) throw error;
  // UWAGA: nie cofamy refurbish_approved_by_user — user juz jawnie zatwierdzil
}

export async function bulkAcceptByAction(
  websiteId: string,
  action: FinalAction,
  userId?: string | null,
): Promise<{ approved: number; cascaded: number }> {
  // Lock wszystkich wpisow z ta akcja (ktore jeszcze nie sa locked)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: posts } = await (supabase as any)
    .from("wp_posts")
    .select("id")
    .eq("website_id", websiteId)
    .eq("final_action", action)
    .eq("final_action_locked", false);

  const ids = ((posts || []) as Array<{ id: string }>).map((p) => p.id);
  if (ids.length === 0) return { approved: 0, cascaded: 0 };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts")
    .update({
      final_action_locked: true,
      final_action_computed_at: new Date().toISOString(),
    })
    .in("id", ids);
  if (error) throw error;

  // Cascade per refurbish_*
  let cascaded = 0;
  if (action === "refurbish" || action === "refurbish_url_preserve") {
    for (const id of ids) {
      const ok = await cascadeRefurbishApproval(id, userId, action);
      if (ok) cascaded++;
    }
  }

  logPublic(PREFIX, `Bulk accept ${action}: ${ids.length} approved, ${cascaded} cascaded`);
  return { approved: ids.length, cascaded };
}
